import { flattenDeep, orderBy } from 'lodash';
import { Mission, Sample, SampleCentroid, SampleSite, SamplingTypes, SoilCore, Zone } from '../db';
import { clearBearingsAround } from '../db_ops/waypoint_bearings';
import logger from '../logger';
import { cleanSet } from '../utils';
import { ft_to_m, MAX_DISTANCE_BETWEEN_CLOSE_CORES_FT } from '../constants';
import { alertFullScreen, alertWarn } from '../alertDispatcher';
import { dispatchMissionUpdated } from '../missionEvents';
import { dispatchCoresNeedUpdate, dispatchPathUpdated, dispatchSamplesUpdated } from '../mapEvents';

class PointsUpdater {
  constructor(
    private mission: Mission,
    private sampling: boolean,
  ) {}

  async update(
    pointsToUpdate: {
      id: string;
      coords: { lat: number; lon: number };
    }[],
  ) {
    await logger.log('UPDATE_POINTS', `sampling=${this.sampling}, points=${JSON.stringify(pointsToUpdate)}`);

    const t1 = performance.now();

    // order the points by sample ID
    try {
      pointsToUpdate = orderBy(
        pointsToUpdate,
        [
          (sel) => {
            if (isNaN(parseInt(sel.id))) {
              throw new Error('cannot parse int');
            }

            return parseInt(sel.id);
          },
        ],
        ['asc'],
      );
    } catch (err) {
      pointsToUpdate = orderBy(pointsToUpdate, ['id'], ['asc']);
    }

    for (const point of pointsToUpdate) {
      const pointSample = this.mission.getSamples().find((sel) => sel.sample_id === point.id);
      const pointSampleSite = pointSample?.getSampleSite();
      const sampleCentroid = pointSampleSite && pointSampleSite.getSampleCentroid();

      if (sampleCentroid) {
        await this.updateCentroid(point, sampleCentroid);
      } else {
        // if we couldn't find a centroid, try to find a core (zone sampling)
        const m = point.id.toString().match(/^([a-zA-Z0-9]+)([,.;-]([1-9][0-9]*))?$/);

        if (!m) {
          continue;
        }

        const allSamples = this.mission.getSamples().filter((sample) => sample.sample_id === m[1]);
        const allCores = cleanSet(flattenDeep(allSamples.map((sel) => sel.getSoilCores())));
        const core = allCores.find((sel) => sel.core_number === parseInt(m[3]));

        if (core) {
          this.updateCore(point, core, m);
        } else {
          // if we couldn't find a centroid and we couldn't find a core, create a sample
          const sample = Sample.create();
          sample.sample_id = point.id.toString();

          const sampleSite = SampleSite.createWithCentroid(
            this.mission,
            sample.sample_id,
            point.coords.lat,
            point.coords.lon,
            '',
          );

          sample.SampleSite_id = sampleSite.instance_id;
          sample.sample_type = SamplingTypes.REGULAR;

          const samplingSpec = this.mission.getPrimarySpec();

          if (samplingSpec) {
            sample.SamplingSpec_id = samplingSpec.instance_id;
          }

          const samplesInSite = cleanSet(flattenDeep(this.mission.getSampleSites().map((site) => site.getSamples())));
          sample.order = Math.max(...samplesInSite.map((sel) => sel.order)) + 1;
        }
      }
    }

    const hasDeletes = pointsToUpdate.some((point) => point.coords.lat === 0.0 && point.coords.lon === 0.0);

    let didRenumber = false;

    if (this.mission.autoRenumberingAllowedBySpecs()) {
      if (!this.sampling && hasDeletes && !this.mission.sample_date) {
        await this.mission.renumber_samples(this.sampling);
        didRenumber = true;
      }
    }

    const t2 = performance.now();

    // call 'to_ros_msg' to re-calculate the nav checksum
    this.mission.to_ros_msg();

    const t3 = performance.now();

    dispatchMissionUpdated();

    if (didRenumber) {
      // renumber_samples changes every sample's id — targeted rebuild
      // can't track them without a full mapping. Fall back to the
      // catch-all core rebuild.
      dispatchCoresNeedUpdate();
    } else {
      // Targeted rebuild: each point.id encodes either a sample_id
      // (centroid) or `sample_id.N` (zone core). Extract the
      // distinct sample_ids so CorePointsLayer / CentroidsLayer only
      // rebuild the affected samples' JSX.
      const affectedSampleIds = Array.from(
        new Set(
          pointsToUpdate.map((p) => {
            const m = p.id.toString().match(/^([a-zA-Z0-9]+)([,.;-]([1-9][0-9]*))?$/);

            return m ? m[1] : p.id;
          }),
        ),
      );
      dispatchSamplesUpdated(affectedSampleIds);
      // centroid.displace moves waypoints — PathLayer needs to redraw
      // the line, but this isn't a full core rebuild.
      dispatchPathUpdated();
    }

    const t4 = performance.now();
    console.log(
      `PointsUpdater.update() took ${t2 - t1} ms to update points, ${t3 - t2} ms to re-calculate checksum, and ${t4 - t3} ms to dispatch event`,
    );
  }

  private updateCore(point: { id: string; coords: { lat: number; lon: number } }, core: SoilCore, m: RegExpMatchArray) {
    if (point.coords.lat === 0.0 && point.coords.lon === 0.0) {
      // remove point
      core.dispose();

      // remove sample site if all cores removed
      const sample = this.mission.getSamples().find((sample) => sample.sample_id === m[1]);

      if (sample?.hasNoCores()) {
        const sampleSite = sample.getSampleSite();
        sampleSite?.dispose();
      }

      // renumber waypoints
      for (const [i, waypoint] of cleanSet(
        orderBy(this.mission.getWaypoints(), ['waypoint_number'], ['asc']),
      ).entries()) {
        waypoint.waypoint_number = i + 1;
      }

      return;
    }

    // TODO SVH getZone() is null, so no sampleZone. Why?
    const sample = core.getSample();
    const zone = sample?.getSampleSite()?.getSampleZone()?.getZone();

    if (!zone) {
      // No zone to validate against — keep current behavior (no move).
      return;
    }

    // Zone missions: dragging any core rigidly translates its whole core
    // cluster (Original + its Extras). Other patterns keep single-core move.
    if (sample && this.mission.is_zone_mission()) {
      this.moveCoreCluster(point, core, sample, zone);

      return;
    }

    if (zone.contains(point.coords.lat, point.coords.lon)) {
      core.lat = point.coords.lat;
      core.lon = point.coords.lon;
      const waypoint = core.getCorePoint()?.getWaypoint();

      if (waypoint) {
        waypoint.lat = point.coords.lat;
        waypoint.lon = point.coords.lon;
        // The hops into and out of a moved core are no longer the planned ones.
        clearBearingsAround(this.mission, [waypoint.waypoint_number]);
      }
    }
  }

  /**
   * Zone-mission cluster move: translate the dragged core's entire core
   * cluster (the Original plus its Extras, grouped by spatial proximity) by
   * the same delta, preserving each core's relative offset. The whole move is
   * rejected if any cluster core would land outside the sample zone.
   */
  private moveCoreCluster(
    point: { id: string; coords: { lat: number; lon: number } },
    core: SoilCore,
    sample: Sample,
    zone: Zone,
  ) {
    const dLat = point.coords.lat - core.lat;
    const dLon = point.coords.lon - core.lon;

    const maxDistance = ft_to_m(MAX_DISTANCE_BETWEEN_CLOSE_CORES_FT);
    const groups = sample.groupCoresByProximity(maxDistance);
    const cluster = groups.find((g) => g.some((c) => c.instance_id === core.instance_id)) ?? [core];

    // Block the whole move if any cluster core would leave the zone.
    const allInside = cluster.every((c) => zone.contains(c.lat + dLat, c.lon + dLon));

    if (!allInside) {
      alertWarn('Cannot move core: a core in the cluster would fall outside its zone.');

      return;
    }

    const movedWaypointNumbers: number[] = [];

    for (const c of cluster) {
      c.lat = c.lat + dLat;
      c.lon = c.lon + dLon;
      const waypoint = c.getCorePoint()?.getWaypoint();

      if (waypoint) {
        waypoint.lat = c.lat;
        waypoint.lon = c.lon;
        movedWaypointNumbers.push(waypoint.waypoint_number);
      }
    }

    // The hops into and out of the moved cluster are no longer the planned ones.
    clearBearingsAround(this.mission, movedWaypointNumbers);
  }

  private async updateCentroid(
    point: { id: string; coords: { lat: number; lon: number } },
    sampleCentroid: SampleCentroid,
  ) {
    if (point.coords.lat === 0.0 && point.coords.lon === 0.0) {
      // remove point
      sampleCentroid.dispose();

      // renumber waypoints
      for (const [i, waypoint] of cleanSet(
        orderBy(this.mission.getWaypoints(), ['waypoint_number'], ['asc']),
      ).entries()) {
        waypoint.waypoint_number = i + 1;
      }
    } else {
      const sampleWithBarcode = sampleCentroid.findSampleWithBarcode();
      let barcode: string = '';

      if (sampleWithBarcode) {
        barcode = sampleWithBarcode.getBarCode()!;
      }

      // update centroid
      await sampleCentroid.displace(point.coords.lat - sampleCentroid.lat, point.coords.lon - sampleCentroid.lon);

      if (barcode) {
        await alertFullScreen('Warning', `Please remove bag ${barcode}`, ['Ok']);
      }
    }
  }
}

export default PointsUpdater;
